'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, Ref, useMemo } from 'react';
import { CentText } from '@/lib/components/Text/CentText';
import { FloatText } from '@/lib/components/Text/FloatText';
import { USDText } from '@/lib/components/Text/USDText';
import { OrderBookSide } from '@/lib/types/order-book';
import { Text } from '@/lib/ui-kit/text';
import { cn } from '@/lib/ui-kit/utils';
import { PriceVolumeCumulative } from '@/lib/utils/getWithCumulativeTotals';

type OrderBookListItemProps = {
  item: PriceVolumeCumulative;
  side: OrderBookSide;
  max: number;
  best?: boolean;
  roundToCents?: boolean;
  ref?: Ref<HTMLDivElement>;
} & ComponentProps<'div'>;

export const OrderBookListItem = ({
  item,
  side,
  max,
  ref,
  best = false,
  roundToCents = false,
  className,
  ...props
}: OrderBookListItemProps) => {
  const t = useTranslations('orderBook');
  const { cumulativeVolume, price, size } = item;

  const scale = useMemo(() => {
    return Math.sqrt(Number(cumulativeVolume) / max);
  }, [max, cumulativeVolume]);

  const isAsks = side === 'asks';

  return (
    <div
      ref={ref}
      className={cn(
        'group relative flex h-9 shrink-0 cursor-pointer flex-row items-center gap-3 px-3 md:px-4',
        isAsks ? 'active:bg-[#fb4e4b]/15' : 'active:bg-[#17d271]/15',
        className,
      )}
      {...props}
    >
      <div className="absolute inset-0 z-0 h-full w-full group-hover:bg-v2-btn-secondary"></div>
      <div
        className={cn(
          'absolute inset-x-0 bottom-[1px] top-[1px] z-0 transition-[width]',
          isAsks ? 'bg-[#fb4e4b]/20' : 'bg-[#17d271]/20',
        )}
        style={{ width: `${scale * 100}%` }}
      />
      <div className="relative flex flex-1">
        {best && isAsks ? (
          <div className="flex h-5 items-center justify-center rounded bg-[#fb4e4b]/20 px-2 text-[#fb4e4b]">
            <Text variant="label-md">{t('asks')}</Text>
          </div>
        ) : null}
        {best && !isAsks ? (
          <div className="flex h-5 items-center justify-center rounded bg-[#17d271]/20 px-2 text-[#17d271]">
            <Text variant="label-md">{t('bids')}</Text>
          </div>
        ) : null}
      </div>
      <CentText
        as="div"
        className={cn(
          'relative min-w-12 flex-1 whitespace-pre text-end text-[13px] font-medium leading-[1.5]',
          isAsks ? 'text-[#fb4e4b]' : 'text-[#17d271]',
        )}
        value={price}
        decimals={1}
      />
      <FloatText
        value={size}
        className="relative min-w-12 flex-1 text-end text-[13px] font-normal leading-[1.5] text-v2-text-primary"
      />
      <USDText
        as="div"
        value={roundToCents ? Math.round(Number(cumulativeVolume)) : cumulativeVolume}
        className="relative flex-1 text-end text-[13px] font-normal leading-[1.5] text-v2-text-primary"
        alwaysShowDecimals={!roundToCents}
      />
    </div>
  );
};
